import { useState, useEffect } from 'react';
import { productService, type Category } from 'services/product.service';

// Module-level cache to ensure categories API is only called once across all component instances
let categoriesCache: Category[] | null = null;
let categoriesPromise: Promise<Category[]> | null = null;

interface UseCategoriesReturn {
  categories: Category[];
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch and cache categories
 * Uses module-level caching to prevent duplicate API calls
 */
export const useCategories = (): UseCategoriesReturn => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      // If categories are already cached, use them immediately
      if (categoriesCache) {
        if (isMounted) {
          setCategories(categoriesCache);
          setLoading(false);
        }
        return;
      }

      // If there's already a request in progress, wait for it
      if (categoriesPromise) {
        try {
          setLoading(true);
          const fetchedCategories = await categoriesPromise;

          if (isMounted) {
            setCategories(fetchedCategories);
            setLoading(false);
          }
        } catch (err) {
          if (isMounted) {
            setError(err instanceof Error ? err : new Error('Failed to fetch categories'));
            setLoading(false);
          }
        }
        return;
      }

      // Make the API call and cache it
      try {
        setLoading(true);
        setError(null);
        categoriesPromise = productService.getCategories();
        const fetchedCategories = await categoriesPromise;

        // Cache the result
        categoriesCache = fetchedCategories;
        categoriesPromise = null;

        if (isMounted) {
          setCategories(fetchedCategories);
          setLoading(false);
        }
      } catch (err) {
        // Clear the promise on error so it can be retried
        categoriesPromise = null;
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch categories'));
          setLoading(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  return { categories, loading, error };
};

